import { useState } from 'react'
import { percentileClim } from '@carbonplan/zarr-layer'
import { useAppStore } from './store'

/**
 * Shared time-mean compute hook. UI-agnostic.
 * Returns { compute, loading, error, clearError }.
 */
export function useComputeMean() {
  const [error, setError] = useState<string | null>(null)

  const compute = async () => {
    const s = useAppStore.getState()
    const zarrLayer = s.zarrLayer as any
    if (!zarrLayer || s.timeMeanLoading) return
    const mod = s.getDatasetModule()
    const layerConfig = mod.buildLayerProps(s.datasetState)
    const timeDim = mod.timeDimension ?? 'time'
    const { [timeDim]: _t, ...selectorWithoutTime } = layerConfig.selector as any
    const timeOpts: Record<string, unknown> = { selector: selectorWithoutTime }
    if (s.timeMeanStartDate && s.timeMeanEndDate && s.reverseTimeIndex) {
      const startIdx = s.reverseTimeIndex(s.timeMeanStartDate)
      const endIdx = s.reverseTimeIndex(s.timeMeanEndDate)
      if (Number.isFinite(startIdx) && Number.isFinite(endIdx)) {
        timeOpts.start = Math.max(0, Math.round(startIdx))
        timeOpts.end = Math.round(endIdx)
      }
    }
    s.setTimeMeanLoading(true)
    s.setTimeMeanEnabled(true)
    setError(null)
    try {
      const result = await zarrLayer.computeTimeMean(timeOpts)
      zarrLayer.setTimeMeanData(result)
      s.setTimeMeanResult(result)
      if (s.timeMeanAutoClim && result.data.length > 0) {
        s.setClim(percentileClim(result.data))
      }
    } catch (e) {
      console.error('Time mean failed', e)
      s.setTimeMeanResult(null)
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      s.setTimeMeanLoading(false)
    }
  }

  const clear = () => {
    const s = useAppStore.getState()
    const zarrLayer = s.zarrLayer as any
    zarrLayer?.setTimeMeanData?.(null)
    s.setTimeMeanResult(null)
    s.setTimeMeanEnabled(false)
    setError(null)
  }

  return { compute, clear, error }
}
